<template>
    <div class="media-preview">
        <!-- Image Preview -->
        <div v-if="mediaType === 'IMAGE'" class="preview-stage">
            <img :src="previewUrl" :alt="media.originalFilename" />
        </div>

        <!-- Video Preview -->
        <div v-else-if="mediaType === 'VIDEO'" class="preview-stage">
            <video controls>
                <source :src="previewUrl" :type="media.mimeType">
                Your browser does not support the video tag.
            </video>
        </div>

        <!-- PDF Preview (show download button) -->
        <div v-else-if="mediaType === 'DOCUMENT' && isPdf" class="preview-stage document-stage">
            <div class="download-center">
                <i class="fal fa-file-pdf fa-4x text-danger mb-3"></i>
                <b-button variant="primary" size="lg" :href="previewUrl" download target="_blank">
                    <i class="fal fa-download"></i> Download PDF
                </b-button>
            </div>
        </div>

        <!-- Generic Document Preview (centered download button) -->
        <div v-else class="preview-stage document-stage">
            <div class="download-center">
                <i :class="getDocumentIcon()" class="fa-4x mb-3"></i>
                <b-button variant="primary" size="lg" :href="previewUrl" download target="_blank">
                    <i class="fal fa-download"></i> Download
                </b-button>
            </div>
        </div>

        <!-- Media Info Footer -->
        <div class="media-info-footer mt-3" v-if="showInfo">
            <div class="info-row">
                <span class="label">Filename:</span>
                <span class="value">{{ media.originalFilename }}</span>
            </div>
            <div class="info-row" v-if="media.fileSize">
                <span class="label">Size:</span>
                <span class="value">{{ formatFileSize(media.fileSize) }}</span>
            </div>
            <div class="info-row" v-if="media.mimeType">
                <span class="label">Type:</span>
                <span class="value">{{ media.mimeType }}</span>
            </div>
            <div class="info-row" v-if="media.createdAt">
                <span class="label">Uploaded:</span>
                <span class="value">{{ formatDate(media.createdAt) }}</span>
            </div>
            <div class="info-row" v-if="media.tags">
                <span class="label">Tags:</span>
                <span class="value">{{ media.tags }}</span>
            </div>
            <div class="info-row" v-if="media.description">
                <span class="label">Description:</span>
                <span class="value">{{ media.description }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MediaPreview',
        props: {
            media: {
                type: Object,
                required: true
            },
            showInfo: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            mediaType() {
                return this.media.mediaType || 'DOCUMENT';
            },
            isPdf() {
                return this.media.mimeType === 'application/pdf' ||
                       (this.media.filename && this.media.filename.toLowerCase().endsWith('.pdf'));
            },
            previewUrl() {
                // Use the public file endpoint for serving files
                return this.fileResourceUrl(this.media.filename);
            }
        },
        methods: {
            getDocumentIcon() {
                const mimeType = this.media.mimeType || '';
                const filename = this.media.filename || '';

                if (mimeType.includes('pdf') || filename.endsWith('.pdf')) {
                    return 'fal fa-file-pdf';
                } else if (mimeType.includes('word') || filename.match(/\.(doc|docx)$/)) {
                    return 'fal fa-file-word';
                } else if (mimeType.includes('excel') || filename.match(/\.(xls|xlsx)$/)) {
                    return 'fal fa-file-excel';
                } else if (mimeType.includes('powerpoint') || filename.match(/\.(ppt|pptx)$/)) {
                    return 'fal fa-file-powerpoint';
                }
                return 'fal fa-file-alt';
            },

            formatFileSize(bytes) {
                if (!bytes) return 'Unknown';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },

            formatDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
            }
        }
    }
</script>

<style lang="scss" scoped>
    .media-preview {
        // Fixed-size preview stage
        .preview-stage {
            width: 100%;
            height: 400px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8f9fa;
            position: relative;
            overflow: hidden;

            // Images - maintain aspect ratio and fit within stage
            img {
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
                object-fit: contain;
            }

            // Videos - maintain aspect ratio and fit within stage
            video {
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
            }

            // Document download center
            &.document-stage {
                .download-center {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;

                    i {
                        opacity: 0.7;
                    }
                }
            }
        }

        .media-info-footer {
            border-top: 1px solid #dee2e6;
            padding-top: 1rem;

            .info-row {
                display: flex;
                margin-bottom: 0.5rem;

                .label {
                    font-weight: 600;
                    min-width: 100px;
                    color: #6c757d;
                }

                .value {
                    flex: 1;
                    word-break: break-word;
                }
            }
        }
    }
</style>
